import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';
import { UpdatePriorityAPI } from '../../projects/api/ProjectService';
import {
  AddAreaAPI,
  DeleteAreaAPI,
  UpdateAreaAPI,
  UpdateAreaPercentValueAPI,
} from '../api/LocationService';
import {
  deleteLocation,
  fetchLocations,
  selectLocationIds,
  selectLocationEntities,
  selectLocationById,
  duplicateLocation,
  selectAllLocationsByProjectId,
} from './locationsSlice';
import {
  deleteZone,
  duplicateZone,
  selectZoneById,
  selectZoneEntities,
  setZoneAreaOrder,
} from './zonesSlice';

/** Create Area
 * @param {*} requestParameters
 * @param {String} requestParameters.name
 * @param {Number} requestParameters.zone_id
 */
export const createArea = createAsyncThunk(
  'areas/createArea',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await AddAreaAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Update Area
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 * @param {Name} requestParameters.name
 */
export const updateArea = createAsyncThunk(
  'areas/updateArea',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await UpdateAreaAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Update Areas Percent
 * @param {*} requestParameters
 * @param {Object[]} requestParameters.areas
 * @param {Number} requestParameters.areas.id
 * @param {Number} requestParameters.areas.percent
 */
export const updateAreasPercent = createAsyncThunk(
  'areas/updateAreasPercent',
  async (requestParameters, { rejectWithValue }) => {
    const apiResponse = await UpdateAreaPercentValueAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

/** Delete Area
 * @param {*} requestParameters
 * @param {Number} requestParameters.id
 */
export const deleteArea = createAsyncThunk(
  'areas/deleteArea',
  async (requestParameters, { rejectWithValue, getState }) => {
    const apiResponse = await DeleteAreaAPI(requestParameters);
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      const id = requestParameters.id;
      const zone = getState().areas.entities[id].zone_id;
      return { zone, area: id };
    }
  }
);

/** Reorder areas
 * @param {*} requestParameters
 * @param {Number} requestParameters.sourceIndex
 * @param {Number} requestParameters.destinationIndex
 * @param {Number} requestParameters.parentId
 */
export const reorderAreas = createAsyncThunk(
  'locations/reorderAreas',
  async (requestParameters, { rejectWithValue, getState, dispatch }) => {
    const parentId = requestParameters.parentId;
    const areaIds = [...getState().zones.entities[parentId].areas];
    let destinationIndex = requestParameters.destinationIndex;
    let sourceIndex = requestParameters.sourceIndex;
    const id = areaIds.splice(sourceIndex, 1);
    areaIds.splice(destinationIndex, 0, ...id);
    dispatch(setZoneAreaOrder({ zoneId: parentId, areas: areaIds }));
    const apiResponse = await UpdatePriorityAPI({
      orderingType: 'area',
      areaIds,
      parentId: parentId,
    });
    if (apiResponse.error) {
      return rejectWithValue(apiResponse.error);
    } else {
      return apiResponse.response.data;
    }
  }
);

export const areasAdapter = createEntityAdapter();

const initialState = areasAdapter.getInitialState({
  error: null,
});

export const areasSlice = createSlice({
  name: 'areas',
  initialState,
  reducers: {
    clearAreas: areasAdapter.removeAll,
    addEmptyArea: (state, action) => {
      areasAdapter.addOne(state, {
        id: action.payload.id,
        zone_id: action.payload.zoneId,
        name: 'New Area',
        density: 0.5,
      });
    },
    removeEmptyArea: (state, action) => {
      areasAdapter.removeOne(state, action.payload.id);
    },
    updateAreaSelection: (state, action) => {
      areasAdapter.upsertMany(state, action.payload);
    },
  },
  extraReducers: (builder) => {
    //Read
    builder.addCase(fetchLocations.fulfilled, (state, action) => {
      if (action.payload.entities?.areas) {
        areasAdapter.setAll(state, action.payload.entities.areas);
      }
    });
    //Create
    builder.addCase(createArea.fulfilled, (state, action) => {
      areasAdapter.removeOne(state, action.meta.arg.id);
      areasAdapter.addOne(state, action.payload);
    });
    builder.addCase(createArea.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Update
    builder.addCase(updateArea.fulfilled, (state, action) => {
      areasAdapter.upsertOne(state, action.payload);
    });
    builder.addCase(updateArea.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Delete
    builder.addCase(deleteArea.fulfilled, (state, action) => {
      areasAdapter.removeOne(state, action.payload.area);
    });
    builder.addCase(deleteArea.rejected, (state, action) => {
      state.error = action.payload;
    });

    //Reorder
    builder.addCase(reorderAreas.fulfilled, (state, action) => {
      areasAdapter.upsertMany(state, action.payload);
    });

    //Location Deleted
    builder.addCase(deleteLocation.fulfilled, (state, action) => {
      areasAdapter.removeMany(state, action.payload.areas);
    });

    //Zone Deleted
    builder.addCase(deleteZone.fulfilled, (state, action) => {
      areasAdapter.removeOne(state, action.payload.area);
    });

    //Duplicate Location
    builder.addCase(duplicateLocation.fulfilled, (state, action) => {
      if (action.payload?.entities?.areas) {
        areasAdapter.upsertMany(state, action.payload.entities.areas);
      }
    });

    //Update areas percent
    builder.addCase(updateAreasPercent.fulfilled, (state, action) => {
      areasAdapter.upsertMany(state, action.payload);
    });

    builder.addCase(duplicateZone.fulfilled, (state, action) => {
      if (action.payload?.entities?.areas) {
        areasAdapter.upsertMany(state, action.payload.entities.areas);
      }
    });
  },
});

export default areasSlice.reducer;

export const { clearAreas, addEmptyArea, removeEmptyArea, updateAreaSelection } =
  areasSlice.actions;

export const selectAreasInGroupByZoneId = createSelector(
  [
    (state, locationId, zoneId) => selectLocationById(state, locationId),
    (state, locationId, zoneId) => selectZoneById(state, zoneId),
    (state, locationId, zoneId) => selectZoneEntities(state),
    (state) => selectAreaEntities(state),
  ],
  (location, zone, zones, areas) => {
    if (location && zone) {
      const responseObj = {
        id: `${location.id} - ${zone.id}`,
        name: `${location.name} - ${zone.name}`,
        children: zone?.areas?.map((id) => areas[id]),
      };
      return [responseObj];
    } else if (location && !zone) {
      const zoneData = location?.zones?.map((id) => {
        const zone = zones[id];
        const children = zone.areas?.map((id) => areas[id]);
        return {
          id: `${location.id} - ${zone.id}`,
          name: `${location.name} - ${zone.name}`,
          locationId: location.id,
          zoneId: zone.id,
          children,
        };
      });

      return zoneData;
    } else {
      return [];
    }
  }
);

export const selectAllAreasInGroup = createSelector(
  [
    (state, projectId) => selectAllLocationsByProjectId(state, projectId),
    (state) => selectZoneEntities(state),
    (state) => selectAreaEntities(state),
  ],
  (locations, zones, areas) => {
    const responseArray = [];
    locations?.forEach((location) => {
      const zone = location?.zones?.map((zoneId) => {
        const zone = zones[zoneId];
        const children = zone?.areas?.map((areaId) => areas[areaId]);
        return {
          id: `${location.id} - ${zone.id}`,
          name: `${location.name} - ${zone.name}`,
          locationId: location.id,
          zoneId: zone.id,
          children,
        };
      });
      responseArray.push(...zone);
    });
    return responseArray;
  }
);

export const selectAllAreaEntitiesWithGroupName = createSelector(
  [
    (state) => selectLocationEntities(state),
    (state) => selectZoneEntities(state),
    (state) => selectAllAreas(state),
  ],
  (locationEntities, zoneEntities, areas) => {
    const areaEntities = {};
    areas?.forEach((area) => {
      const zone = zoneEntities[area.zone_id];
      const location_id = zoneEntities[area.zone_id].location_id;
      const location = locationEntities[location_id];
      areaEntities[area.id] = { ...area, groupName: `${location.name} - ${zone.name}` };
      return areaEntities;
    });
    return areaEntities;
  }
);

/** Selectors **/
export const {
  selectById: selectAreaById,
  selectIds: selectAreaIds,
  selectEntities: selectAreaEntities,
  selectAll: selectAllAreas,
  selectTotal: selectTotalAreas,
} = areasAdapter.getSelectors((state) => state.areas);
